import { FilterListRounded, QrCodeScannerRounded } from "@mui/icons-material";
import {
  Autocomplete,
  Badge,
  Box,
  Checkbox,
  ClickAwayListener,
  IconButton,
  Popper,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  useTheme,
} from "@mui/material";
import { Dispatch, SetStateAction, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useInventory } from "../../../InventoryProvider";
import { getSelectedRows } from "../../../../../helpers/utils";

type EnhancedTableHeadTypes = {
  handleSelectAllCheckbox: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleFilterChange: (
    headCell: string,
    listOfScannedQr: (string | number)[]
  ) => void;
  selectedValues: Record<string, string[] | number[]>;
  setQrScannerToggle: Dispatch<SetStateAction<boolean>>;
  batchEditMode: boolean;
};

const EnhancedTableHead: React.FC<EnhancedTableHeadTypes> = ({
  handleSelectAllCheckbox,
  handleFilterChange,
  selectedValues,
  setQrScannerToggle,
  batchEditMode,
}) => {
  const [search, setSearch] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const { rows, filteredRows, headCells, setHeadCells } = useInventory();

  const handleSearchToggle = (
    event: React.MouseEvent<HTMLElement>,
    headid: string
  ) => {
    setSearch(search == headid ? null : headid);
    setAnchorEl(anchorEl == event.currentTarget ? null : event.currentTarget);
    setOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const theme = useTheme();
  const { t } = useTranslation();

  const selectedRows = getSelectedRows(filteredRows);

  return (
    <>
      <TableHead>
        <TableRow>
          <TableCell padding="checkbox" sx={{ backgroundColor: "inherit" }}>
            <Checkbox
              indeterminate={
                selectedRows.length > 0 &&
                selectedRows.length < filteredRows.length
              }
              checked={
                filteredRows.length! > 0 &&
                selectedRows.length == filteredRows.length
              }
              onChange={handleSelectAllCheckbox}
            />
          </TableCell>
          {headCells
            .filter((h) => h.visible)
            .map((headCell) => (
              <TableCell key={headCell.id} sx={{ backgroundColor: "inherit" }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  {t(headCell.label)}
                  {headCell.id !== "actions" && (
                    <IconButton
                      onClick={(event) => {
                        handleSearchToggle(event, headCell.id);
                      }}
                    >
                      <Badge
                        badgeContent={
                          selectedValues[headCell.id]
                            ? selectedValues[headCell.id].length
                            : null
                        }
                        color="primary"
                      >
                        <FilterListRounded />
                      </Badge>
                    </IconButton>
                  )}

                  {headCell.id !== "actions" && search == headCell.id && (
                    <ClickAwayListener
                      onClickAway={() => {
                        setSearch(null);
                        setAnchorEl(null);
                      }}
                    >
                      <Popper
                        id="0"
                        open={search === headCell.id}
                        anchorEl={anchorEl}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                          zIndex: 5,
                          backgroundColor: theme.palette.background.default,
                        }}
                      >
                        {headCell.id === "serial" && (
                          <Tooltip title={t("inventory.scanQrCodeToSearch")}>
                            <IconButton
                              onClick={() => setQrScannerToggle(true)}
                              sx={{
                                boxShadow: "hsla(210, 8%, 60%, 1) 0px 2px 10px",
                              }}
                            >
                              <QrCodeScannerRounded />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Autocomplete
                          multiple
                          disableCloseOnSelect
                          open={open}
                          onOpen={() => setOpen(true)}
                          onClose={() => setOpen(false)}
                          sx={{ width: 300 }}
                          value={selectedValues[headCell.id] || []}
                          onChange={(_, newValues) => {
                            handleFilterChange(headCell.id, newValues);
                          }}
                          options={[
                            ...new Set(
                              rows
                                .map((r) => headCell.getValue(r))
                                .filter(
                                  (value) =>
                                    value !== null && value !== undefined
                                )
                            ),
                          ]}
                          getOptionLabel={(option) => option.toString()}
                          renderInput={(params) => (
                            <TextField
                              inputRef={inputRef}
                              variant="filled"
                              {...params}
                              size="small"
                              label={t("inventory.search")}
                            />
                          )}
                        />
                      </Popper>
                    </ClickAwayListener>
                  )}
                </Box>
              </TableCell>
            ))}
        </TableRow>
      </TableHead>
    </>
  );
};

export default EnhancedTableHead;
